/**
 * SourceSection — Card-Header, der erklaert WEM die nachfolgenden Felder gehoeren.
 *
 * Eine Geste, durchgaengig:
 *   - Eyebrow (Caps) mit dem Truth-Owner
 *   - Subtitle in einer Zeile: wofuer die Felder benutzt werden
 *   - Hairline drunter
 *
 * Damit muessen wir nicht an jedem Feld ein Lock-Icon oder Badge kleben — der
 * Section-Header sagt einmal Bescheid, die Felder bleiben ruhig. Linear-Pattern.
 *
 * Sources:
 *   - channex     → Werte werden zu Channex gepusht (von dort an Airbnb/Booking)
 *   - internal    → Bleiben in StayOn, beeinflussen Cleaning/Invoice/Mailing
 *   - system      → Vom System verwaltet, vom User nur lesbar
 *   - ota-mirror  → Gespiegelt aus einer Buchung (Phase 5, Booking-Detail)
 */
import type { ReactNode } from 'react';
import { Eyebrow } from './Eyebrow';
import { cn } from '@/shared/lib/cn';

export type SectionSource = 'channex' | 'internal' | 'system' | 'ota-mirror';

const SOURCE_LABELS: Record<SectionSource, { eyebrow: string; subtitle: string }> = {
  channex: {
    eyebrow: 'Pushed to Channels',
    subtitle: 'Sichtbar auf Airbnb, Booking.com — via Channex.',
  },
  internal: {
    eyebrow: 'Internal',
    subtitle: 'Nur in StayOn. Steuert Reinigung, Rechnungen, Gäste-Mails.',
  },
  system: {
    eyebrow: 'System',
    subtitle: 'Automatisch verwaltet. Nur lesbar.',
  },
  'ota-mirror': {
    eyebrow: 'Aus Buchung gespiegelt',
    subtitle: 'Read-only. Quelle ist die OTA-Buchung.',
  },
};

export interface SourceSectionProps {
  source: SectionSource;
  /** Optional: Eyebrow ueberschreiben (z.B. "Pushed to Channels · Identitaet"). */
  eyebrow?: string;
  /** Optional: Subtitle ueberschreiben fuer kontextspezifische Hinweise. */
  subtitle?: ReactNode;
  /** Rechte Action am Header (z.B. Status-Pill, Edit-Button). */
  trailing?: ReactNode;
  className?: string;
}

export function SourceSection({
  source,
  eyebrow,
  subtitle,
  trailing,
  className,
}: SourceSectionProps) {
  const defaults = SOURCE_LABELS[source];
  return (
    <header className={cn('border-line/50 mb-5 border-b pb-3', className)}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <Eyebrow variant="muted">{eyebrow ?? defaults.eyebrow}</Eyebrow>
          <p className="text-ink-soft mt-1.5 text-xs leading-relaxed">
            {subtitle ?? defaults.subtitle}
          </p>
        </div>
        {trailing && <div className="shrink-0">{trailing}</div>}
      </div>
    </header>
  );
}
